import type { ChildProcess } from 'node:child_process'
import * as fsp from 'node:fs/promises'
import * as os from 'node:os'
import * as path from 'node:path'
import {
	type AgentSummary,
	type DashboardServer,
	createDashboardServer,
} from '@openvole/dashboard-server'
import { execa } from 'execa'
import { loadConfig } from '../config/index.js'
import { createLogger } from '../core/logger.js'
import { agentFromSession, agentSessionId } from '../core/reply-address.js'
import { type FileRootKey, ProjectFiles } from '../project/files.js'
import { scanProjectRoot } from '../project/scan.js'
import { ProjectStore } from '../project/store.js'
import { TaskStore } from '../project/tasks.js'
import { EventLog, dayKey } from './event-log.js'
import { AgentManager } from './manager.js'

const logger = createLogger('control-plane')

/** True when `target` is one of `roots` or sits beneath one. */
function isInsideAny(target: string, roots: string[]): boolean {
	return roots.some((root) => {
		const resolved = path.resolve(root)
		return target === resolved || target.startsWith(resolved + path.sep)
	})
}
const RPC_TIMEOUT_MS = 15_000
/** For control requests that invoke the brain. A CLI-backed brain routinely takes minutes. */
const BRAIN_RPC_TIMEOUT_MS = 600_000
const STOP_GRACE_MS = 5000
const STATE_DEBOUNCE_MS = 150
/** Max tasks included in an orchestrator's agent_state summary. */
const ORCH_STATE_TASKS = 10
/** Max chars of a task's input echoed in an orchestrator's agent_state summary. */
const ORCH_INPUT_CLIP = 200

interface Pending {
	resolve: (value: unknown) => void
	reject: (err: Error) => void
	timeout: ReturnType<typeof setTimeout>
}

interface AgentChild {
	proc: ChildProcess
	pending: Map<number, Pending>
	nextId: number
	ready: Promise<void>
	markReady: () => void
}

export interface ControlPlaneOptions {
	/** Absolute path to the running dist/cli.js (the `__run-agent` daemon entry). */
	cliPath: string
	port: number
	home?: string
	/** Interface to bind (default 127.0.0.1 — set 0.0.0.0 to expose, paired with a token). */
	host?: string
	/** Session token required on the dashboard page, WebSocket, and panel routes. */
	token?: string
}

/**
 * The single control-plane web server. Spawns one engine subprocess (IPC child) per
 * running agent, aggregates their state/events, and hosts ONE dashboard for all of them.
 */
export class ControlPlane {
	private readonly manager: AgentManager
	private readonly children = new Map<string, AgentChild>()
	private readonly refreshTimers = new Map<string, ReturnType<typeof setTimeout>>()
	private readonly eventLog: EventLog
	private readonly cliPath: string
	private readonly port: number
	private readonly host?: string
	private readonly token?: string
	private server: DashboardServer | undefined
	private availablePawsCache:
		| Array<{ name: string; version: string; description: string }>
		| undefined

	constructor(opts: ControlPlaneOptions) {
		this.cliPath = opts.cliPath
		this.port = opts.port
		this.host = opts.host
		this.token = opts.token
		this.manager = new AgentManager(opts.home ? { home: opts.home } : undefined)
		// Server-wide, next to the registry: one feed covering every agent, which is how the
		// dashboard shows it.
		this.eventLog = new EventLog(path.join(this.manager.homeDir, '.openvole', 'logs'))
	}

	start(): void {
		this.server = createDashboardServer(
			this.port,
			{
				listAgents: () => this.listAgents(),
				startAgent: (id) => this.startAgent(id),
				stopAgent: (id) => this.stopAgent(id),
				createAgent: (name) => this.createAgent(name),
				renameAgent: (id, name) => this.renameAgent(id, name),
				projectList: (agentId, status) => this.projectList(agentId, status as never),
				projectOpen: (agentId, id) => this.projectOpen(agentId, id),
				projectScan: (agentId, root) => this.projectScan(agentId, root),
				listDirectories: (agentId, dirPath) => this.listDirectories(agentId, dirPath),
				grantPath: (agentId, dirPath) => this.grantPath(agentId, dirPath),
				projectCreate: (agentId, input) => this.projectCreate(agentId, input),
				projectUpdate: (agentId, id, patch) => this.projectUpdate(agentId, id, patch),
				projectArchive: (agentId, id) => this.projectArchive(agentId, id),
				projectFiles: (agentId, id, op, args) => this.projectFiles(agentId, id, op, args),
				resolveProjectUpload: (agentId, projectId, root, dir, name) =>
					this.resolveProjectUpload(agentId, projectId, root, dir, name),
				taskAdd: (agentId, input) => this.taskAdd(agentId, input),
				taskRun: (agentId, projectId, taskId) =>
					this.callAgent(agentId, 'project_task_run', { projectId, taskId }),
				taskUpdate: (agentId, projectId, taskId, patch) =>
					this.taskUpdate(agentId, projectId, taskId, patch),
				removeAgent: (id) => this.removeAgent(id),
				fetchState: (id) => this.callAgent(id, 'state'),
				readConfig: (id) => this.callAgent(id, 'read_config'),
				writeConfig: (config, id) => this.callAgent(id, 'write_config', { config }),
				readIdentity: (id) => this.callAgent(id, 'read_identity'),
				writeIdentity: (filename, content, id) =>
					this.callAgent(id, 'write_identity', { filename, content }),
				restartEngine: (id) => this.callAgent(id, 'restart'),
				listAvailablePaws: () => this.listAvailablePaws(),
				installPaw: (name, id) => this.installPawInAgent(id, name),
				submitTask: (input, sessionId, id) => this.callAgent(id, 'submit', { input, sessionId }),
				chatHistory: (sessionId, id) => this.callAgent(id, 'chat_history', { sessionId }),
				chatSessions: (id) => this.callAgent(id, 'chat_sessions'),
				chatClear: (sessionId, id) => this.callAgent(id, 'chat_clear', { sessionId }),
				// Runs the brain — give it the same room a think gets, not the 15s lookup deadline.
				chatCompact: (sessionId, keepLast, id) =>
					this.callAgent(id, 'chat_compact', { sessionId, keepLast }, BRAIN_RPC_TIMEOUT_MS),
				volenetInstances: (id) => this.callAgent(id, 'volenet_instances'),
				volenetChatHistory: (peerId, id) => this.callAgent(id, 'volenet_chat_history', { peerId }),
				volenetChatStatus: (id) => this.callAgent(id, 'volenet_chat_status'),
				volenetChatSend: (peerId, text, id) =>
					this.callAgent(id, 'volenet_chat_send', { peerId, text }),
				volenetChatClear: (peerId, id) => this.callAgent(id, 'volenet_chat_clear', { peerId }),
				volenetRelayMembers: (id) => this.callAgent(id, 'volenet_relay_members'),
				volenetRelayRequests: (id) => this.callAgent(id, 'volenet_relay_requests'),
				volenetRelayConnect: (peerId, note, id) =>
					this.callAgent(id, 'volenet_relay_connect', { peerId, note }),
				volenetRelayApprove: (peerId, id) =>
					this.callAgent(id, 'volenet_relay_approve', { peerId }),
				volenetRelayDeny: (peerId, id) => this.callAgent(id, 'volenet_relay_deny', { peerId }),
				volenetRelayRevoke: (peerId, id) => this.callAgent(id, 'volenet_relay_revoke', { peerId }),
				volenetFileSend: (peerId, filePath, note, id) =>
					this.callAgent(id, 'volenet_file_send', { peerId, path: filePath, note }),
				volenetFileAccept: (transferId, id) =>
					this.callAgent(id, 'volenet_file_accept', { transferId }),
				volenetFileReject: (transferId, id) =>
					this.callAgent(id, 'volenet_file_reject', { transferId }),
				volenetFileStatus: (id) => this.callAgent(id, 'volenet_file_status'),
				volenetPairRequests: (id) => this.callAgent(id, 'volenet_pair_requests'),
				volenetPairAccept: (ref, id) => this.callAgent(id, 'volenet_pair_accept', { ref }),
				volenetPairDeny: (ref, id) => this.callAgent(id, 'volenet_pair_deny', { ref }),
				volenetPairProbe: (url, id) => this.callAgent(id, 'volenet_pair_probe', { url }),
				volenetPairInitiate: (url, publicKey, note, id) =>
					this.callAgent(id, 'volenet_pair_initiate', { url, publicKey, note }),
				volenetJoinHub: (url, id) => this.callAgent(id, 'volenet_join_hub', { url }),
				resolveUploadDir: async (agentId) => {
					const reg = await this.manager.readRegistry()
					const entry = reg.agents.find((s) => s.id === agentId || s.name === agentId)
					if (!entry) throw new Error(`Unknown agent: ${agentId}`)
					return path.join(entry.path, '.openvole', 'net', 'files', 'outbox')
				},
				eventLogDays: () => this.eventLog.listDays(),
				// The server's local day — the browser may sit in another timezone, and "today"
				// has to mean the file events are being appended to right now.
				eventLogToday: () => dayKey(new Date()),
				eventLogRead: (day, tail) => this.eventLog.read(day, tail),
				eventLogPath: (day) => this.eventLog.fileFor(day),
				getPanelHtml: (agentId, paw) => this.callAgent(agentId, 'panel_html', { paw }),
				listMcpTools: (agentId) => this.callAgent(agentId, 'tools_mcp'),
				callPawTool: (agentId, name, params) => this.callAgent(agentId, 'tool', { name, params }),
			},
			{ host: this.host, token: this.token },
		)
	}

	async listAgents(): Promise<AgentSummary[]> {
		const reg = await this.manager.readRegistry()
		return reg.agents.map((s) => ({
			id: s.id,
			name: s.name,
			state: this.children.has(s.id) ? 'running' : 'stopped',
			pid: this.children.get(s.id)?.proc.pid,
			orchestrator: s.orchestrator === true,
		}))
	}

	async startAgent(id: string): Promise<{ ok: true }> {
		if (this.children.has(id)) return { ok: true }
		const reg = await this.manager.readRegistry()
		const entry = reg.agents.find((s) => s.id === id || s.name === id)
		if (!entry) throw new Error(`Agent not found: ${id}`)

		const proc = execa('node', [this.cliPath, '__run-agent', entry.path], {
			cwd: entry.path,
			// Tell the engine (and its paws, e.g. a Claude Code brain) where the control plane's
			// MCP endpoint lives, so it can expose this agent's tools back to an MCP client.
			env: {
				...process.env,
				VOLE_DASHBOARD_URL: `http://127.0.0.1:${this.port}`,
				VOLE_AGENT_ID: entry.id,
				// Legacy name — published paws (paw-brain's claude-code provider) still read it.
				VOLE_SPACE_ID: entry.id,
				// Always set explicitly ('1'/'0') so a stray VOLE_ORCHESTRATOR in the serve
				// process's own env can't leak orchestrator tooling into every agent. The
				// authoritative check stays parent-side (registry flag, per request).
				VOLE_ORCHESTRATOR: entry.orchestrator ? '1' : '0',
				...(this.token ? { VOLE_DASHBOARD_TOKEN: this.token } : {}),
			},
			stdio: ['ignore', 'ignore', 'ignore', 'ipc'],
			reject: false,
			cleanup: true,
		}) as unknown as ChildProcess

		let markReady: () => void = () => {}
		const ready = new Promise<void>((resolve) => {
			markReady = resolve
		})
		setTimeout(markReady, 8000) // fallback if the engine never signals ready
		const child: AgentChild = { proc, pending: new Map(), nextId: 1, ready, markReady }
		this.children.set(entry.id, child)
		proc.on('message', (msg) => this.onChildMessage(entry.id, msg))
		proc.on('exit', () => this.onChildExit(entry.id))
		logger.info(`Started agent "${entry.id}" (pid ${proc.pid})`)
		this.broadcastAgents()
		return { ok: true }
	}

	async stopAgent(id: string): Promise<{ ok: true }> {
		const child = this.children.get(id)
		if (!child) return { ok: true }
		this.children.delete(id)
		for (const p of child.pending.values()) {
			clearTimeout(p.timeout)
			p.reject(new Error('Agent stopped'))
		}
		child.pending.clear()
		const { proc } = child
		proc.kill('SIGTERM')
		setTimeout(() => {
			if (!proc.killed) proc.kill('SIGKILL')
		}, STOP_GRACE_MS)
		this.broadcastAgents()
		return { ok: true }
	}

	async createAgent(name: string): Promise<{ ok: true; id: string; name: string }> {
		const entry = await this.manager.create(name)
		this.broadcastAgents()
		return { ok: true, id: entry.id, name: entry.name }
	}

	/**
	 * Rename an agent's display name. The id — and therefore the directory, the running engine's
	 * VOLE_AGENT_ID, and its MCP endpoint — is left alone, so a running agent keeps working and
	 * needs no restart.
	 */
	async renameAgent(id: string, name: string): Promise<{ ok: true; id: string; name: string }> {
		const entry = await this.manager.rename(id, name)
		this.broadcastAgents()
		return { ok: true, id: entry.id, name: entry.name }
	}

	/**
	 * Open an agent's project stores.
	 *
	 * Projects are files in the agent's own directory, so the control plane reads and writes them
	 * directly instead of round-tripping through the agent process. That means the dashboard shows
	 * an agent's projects and tasks while it is **stopped** — which is exactly when you want to
	 * queue work for it — and needs no IPC surface for any of this.
	 *
	 * Safe to do concurrently with a running agent: neither store caches, manifests are written
	 * atomically, and the task log is append-only with last-line-wins. Two writers racing the same
	 * task can lose one update; they cannot corrupt the file.
	 */
	private async projectStoresFor(
		agentId: string,
	): Promise<{ projects: ProjectStore; tasks: TaskStore }> {
		const reg = await this.manager.readRegistry()
		const entry = reg.agents.find(
			(a) => a.id === agentId || a.name.toLowerCase() === agentId.toLowerCase(),
		)
		if (!entry) throw new Error(`Agent not found: "${agentId}"`)

		let allowedPaths: string[] = []
		try {
			const config = await loadConfig(path.resolve(entry.path, 'vole.config.json'))
			allowedPaths = config.security?.allowedPaths ?? []
		} catch {
			// An unreadable config only limits external roots, not listing what already exists.
		}

		const projects = new ProjectStore(path.resolve(entry.path, '.openvole', 'workspace'), {
			agentRoot: entry.path,
			allowedPaths,
		})
		await projects.init()
		return { projects, tasks: new TaskStore(projects) }
	}

	/** Projects with their open-task counts — what the dashboard's Projects tab lists. */
	async projectList(agentId: string, status?: 'active' | 'paused' | 'archived' | 'all') {
		const { projects, tasks } = await this.projectStoresFor(agentId)
		const list = await projects.list(status ? { status } : undefined)
		return {
			ok: true as const,
			projects: await Promise.all(
				list.map(async (p) => ({
					...p,
					dir: projects.dirFor(p.id),
					openTasks: (await tasks.list({ projectId: p.id })).length,
				})),
			),
		}
	}

	async projectOpen(agentId: string, id: string) {
		const { projects, tasks } = await this.projectStoresFor(agentId)
		const project = await projects.get(id)
		if (!project) throw new Error(`No such project: "${id}"`)
		return {
			ok: true as const,
			project,
			dir: projects.dirFor(id),
			contextFiles: (await projects.readContextFiles(id, project.contextFiles)).inlined,
			tasks: await tasks.list({ projectId: id, state: 'all' }),
			// Reconstructed from the same tasks.jsonl the list came from — one extra pass over a
			// file already in the page cache, not a second round trip per task card.
			history: Object.fromEntries(await tasks.history(id)),
		}
	}

	/**
	 * List sub-directories of a path, for the dashboard's directory picker.
	 *
	 * A browser cannot supply an absolute path — `webkitdirectory` gives relative names and the
	 * File System Access API gives an opaque handle — so choosing a project root has to be done
	 * against the filesystem the agent actually runs on.
	 *
	 * Not restricted to `allowedPaths`, deliberately: the whole point is to pick a directory that
	 * is *not* granted yet and then grant it. That is not a widening of what this dashboard can
	 * already do — `writeConfig` lets the same operator set `allowedPaths` to `/` outright — so a
	 * lister is strictly weaker than what the token already carries. Each response says whether
	 * the path is currently granted so the UI can be honest about it.
	 */
	async listDirectories(agentId: string, dirPath?: string) {
		const { projects } = await this.projectStoresFor(agentId)
		const target = path.resolve(dirPath?.trim() || os.homedir())

		const entries = await fsp.readdir(target, { withFileTypes: true })
		const dirs: Array<{ name: string; path: string }> = []
		for (const entry of entries) {
			// Skip dotfiles: they are noise in a project picker, and .git/node_modules dominate.
			if (entry.name.startsWith('.')) continue
			if (!entry.isDirectory()) {
				// A symlink to a directory is a legitimate project root; readdir types it as a link.
				if (!entry.isSymbolicLink()) continue
				try {
					if (!(await fsp.stat(path.join(target, entry.name))).isDirectory()) continue
				} catch {
					continue
				}
			}
			dirs.push({ name: entry.name, path: path.join(target, entry.name) })
		}
		dirs.sort((a, b) => a.name.localeCompare(b.name))

		const parent = path.dirname(target)
		return {
			ok: true as const,
			path: target,
			parent: parent === target ? null : parent,
			home: os.homedir(),
			dirs,
			allowed: isInsideAny(target, projects.allowedRoots),
			allowedRoots: projects.allowedRoots,
		}
	}

	/**
	 * Add a path to the agent's `security.allowedPaths`.
	 *
	 * Written straight to vole.config.json rather than through the running engine, so it works on
	 * a stopped agent like the rest of the projects surface. This is a human action from an
	 * authenticated dashboard — the boundary it protects is the *agent* granting itself reach, not
	 * the operator doing it deliberately.
	 */
	async grantPath(agentId: string, dirPath: string) {
		const reg = await this.manager.readRegistry()
		const entry = reg.agents.find(
			(a) => a.id === agentId || a.name.toLowerCase() === agentId.toLowerCase(),
		)
		if (!entry) throw new Error(`Agent not found: "${agentId}"`)

		const resolved = path.resolve(dirPath)
		const stat = await fsp.stat(resolved).catch(() => null)
		if (!stat?.isDirectory()) throw new Error(`Not a directory: ${resolved}`)

		const configPath = path.resolve(entry.path, 'vole.config.json')
		const raw = JSON.parse(await fsp.readFile(configPath, 'utf-8')) as Record<string, unknown>
		const security = (raw.security as Record<string, unknown> | undefined) ?? {}
		const current = Array.isArray(security.allowedPaths) ? (security.allowedPaths as string[]) : []

		if (!current.includes(resolved)) {
			security.allowedPaths = [...current, resolved]
			raw.security = security
			await fsp.writeFile(configPath, `${JSON.stringify(raw, null, 2)}\n`, 'utf-8')
			logger.info(`Granted ${resolved} to agent ${entry.id}`)
		}

		return {
			ok: true as const,
			path: resolved,
			allowedPaths: security.allowedPaths as string[],
			// allowedPaths is read when the engine loads its config, so a running agent keeps the
			// old set until it restarts. The control plane re-reads per request and is current.
			restartRequired: this.children.has(entry.id),
		}
	}

	async projectScan(agentId: string, root: string) {
		const { projects } = await this.projectStoresFor(agentId)
		return { ok: true as const, ...(await scanProjectRoot(root, projects.allowedRoots)) }
	}

	async projectCreate(agentId: string, input: Record<string, unknown>) {
		const { projects } = await this.projectStoresFor(agentId)
		const project = await projects.create(input as never)
		this.broadcastAgents()
		return { ok: true as const, project }
	}

	async projectUpdate(agentId: string, id: string, patch: Record<string, unknown>) {
		const { projects } = await this.projectStoresFor(agentId)
		const { context, ...rest } = patch as { context?: string; [k: string]: unknown }
		const hasPatch = Object.values(rest).some((v) => v !== undefined)
		const project = hasPatch ? await projects.update(id, rest as never) : await projects.get(id)
		if (!project) throw new Error(`No such project: "${id}"`)
		if (context !== undefined) await projects.writeContext(id, context)
		return { ok: true as const, project }
	}

	async projectArchive(agentId: string, id: string) {
		const { projects } = await this.projectStoresFor(agentId)
		return { ok: true as const, project: await projects.archive(id) }
	}

	/**
	 * The project file browser and manager — one entry point, dispatching on `op`.
	 *
	 * Kept as a single method rather than nine callbacks because every operation takes the same
	 * (agent, project, root, path) address and differs only in verb; splitting them multiplied the
	 * plumbing without separating anything. `ProjectFiles` holds the boundary — this only routes.
	 */
	async projectFiles(
		agentId: string,
		projectId: string,
		op: string,
		args: Record<string, unknown> = {},
	) {
		const { projects } = await this.projectStoresFor(agentId)
		const files = new ProjectFiles(projects)
		const root = (args.root as FileRootKey) || 'workspace'
		const rel = typeof args.path === 'string' ? args.path : ''

		switch (op) {
			case 'roots':
				return { ok: true as const, roots: await files.roots(projectId) }
			case 'list':
				return {
					ok: true as const,
					roots: await files.roots(projectId),
					listing: await files.list(projectId, root, rel),
				}
			case 'read':
				return { ok: true as const, file: await files.read(projectId, root, rel) }
			case 'write':
				return {
					ok: true as const,
					...(await files.write(projectId, root, rel, String(args.content ?? ''))),
				}
			case 'create':
				return { ok: true as const, ...(await files.create(projectId, root, rel)) }
			case 'mkdir':
				return { ok: true as const, ...(await files.mkdir(projectId, root, rel)) }
			case 'delete':
				return { ok: true as const, ...(await files.remove(projectId, root, rel)) }
			case 'rename':
				return {
					ok: true as const,
					...(await files.rename(projectId, root, rel, String(args.to ?? ''))),
				}
			default:
				throw new Error(`Unknown file operation: "${op}"`)
		}
	}

	/**
	 * Where an uploaded file should land inside a project, authorized before a byte is written.
	 *
	 * The upload route streams straight to this path, so the whole boundary check happens here —
	 * the HTTP layer never joins a client-supplied name onto a directory itself.
	 */
	async resolveProjectUpload(
		agentId: string,
		projectId: string,
		root: string,
		dir: string,
		name: string,
	): Promise<string> {
		const { projects } = await this.projectStoresFor(agentId)
		const files = new ProjectFiles(projects)
		const target = await files.uploadTarget(
			projectId,
			(root as FileRootKey) || 'workspace',
			dir,
			name,
		)
		return target.path
	}

	async taskAdd(agentId: string, input: Record<string, unknown>) {
		const { tasks } = await this.projectStoresFor(agentId)
		const { projectId, goal, doneCriteria, priority, maxIterations } = input as {
			projectId: string
			goal: string
			doneCriteria?: string[]
			priority?: number
			maxIterations?: number
		}
		const task = await tasks.create({
			projectId,
			goal,
			doneCriteria,
			priority,
			...(maxIterations ? { budget: { maxIterations } } : {}),
		})
		return { ok: true as const, task }
	}

	async taskUpdate(
		agentId: string,
		projectId: string,
		taskId: string,
		patch: Record<string, unknown>,
	) {
		const { tasks } = await this.projectStoresFor(agentId)
		const defined = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined))
		return { ok: true as const, task: await tasks.update(projectId, taskId, defined as never) }
	}

	async removeAgent(id: string): Promise<{ ok: true }> {
		const proc = this.children.get(id)?.proc
		await this.stopAgent(id)
		// Wait for the engine child to actually exit before deleting its files, so a late
		// shutdown write can't recreate the directory after we remove it.
		if (proc && proc.exitCode == null) {
			await new Promise<void>((resolve) => {
				const t = setTimeout(resolve, STOP_GRACE_MS + 500)
				proc.once('exit', () => {
					clearTimeout(t)
					resolve()
				})
			})
		}
		await this.manager.remove(id, { purge: true })
		this.broadcastAgents()
		return { ok: true }
	}

	/** Official @openvole/paw-* packages from the npm registry (cached for the process). */
	async listAvailablePaws(): Promise<
		Array<{ name: string; version: string; description: string }>
	> {
		if (this.availablePawsCache) return this.availablePawsCache
		const url = `https://registry.npmjs.org/-/v1/search?text=${encodeURIComponent('@openvole/paw')}&size=250`
		const res = await fetch(url)
		if (!res.ok) throw new Error(`npm registry returned ${res.status}`)
		const data = (await res.json()) as {
			objects?: Array<{ package?: { name?: string; version?: string; description?: string } }>
		}
		const paws = (data.objects ?? [])
			.map((o) => o.package)
			.filter(
				(p): p is { name: string; version?: string; description?: string } =>
					typeof p?.name === 'string' &&
					p.name.startsWith('@openvole/paw-') &&
					p.name !== '@openvole/paw-sdk',
			)
			.map((p) => ({ name: p.name, version: p.version ?? '', description: p.description ?? '' }))
			.sort((a, b) => a.name.localeCompare(b.name))
		this.availablePawsCache = paws
		return paws
	}

	/** Install a paw from npm into an agent (npm install + register with default permissions). */
	async installPawInAgent(agentId: string | undefined, name: string): Promise<unknown> {
		if (!agentId) throw new Error('No agent selected')
		const reg = await this.manager.readRegistry()
		const entry = reg.agents.find((s) => s.id === agentId || s.name === agentId)
		if (!entry) throw new Error(`Agent not found: ${agentId}`)
		const { installPaw } = await import('../paw/install.js')
		return installPaw(entry.path, name)
	}

	private callAgent(
		id: string | undefined,
		method: string,
		params: Record<string, unknown> = {},
		/**
		 * Override the default deadline. Nearly every control request is a lookup and 15s is
		 * generous; the exceptions are the ones that run the brain, where 15s is a guaranteed
		 * failure — a CLI-backed brain routinely takes minutes.
		 */
		timeoutMs = RPC_TIMEOUT_MS,
	): Promise<unknown> {
		const child = id ? this.children.get(id) : undefined
		if (!id || !child) {
			return Promise.reject(new Error(id ? `Agent not running: ${id}` : 'No agent selected'))
		}
		const reqId = child.nextId++
		return child.ready.then(
			() =>
				new Promise<unknown>((resolve, reject) => {
					const timeout = setTimeout(() => {
						child.pending.delete(reqId)
						reject(new Error(`Control request timed out: ${method}`))
					}, timeoutMs)
					child.pending.set(reqId, { resolve, reject, timeout })
					child.proc.send?.({ id: reqId, method, params })
				}),
		)
	}

	/**
	 * Answer a reverse-RPC request from an orchestrator agent. The sender's authority is
	 * checked against the registry on EVERY request (fresh read), so revoking the flag via
	 * `vole agent orchestrate <name> off` takes effect immediately. Never throws — errors go
	 * back to the sender as `{cres:{id,error}}`. Public (with an injectable reply) for tests.
	 */
	/**
	 * Carry a finished run's answer back to the agent that asked for it.
	 *
	 * This is what makes a cross-agent reply address mean anything. A run started by another
	 * agent's message answers that agent, but the answer is produced inside the worker and would
	 * otherwise land in a transcript nobody else reads — which is the original complaint: when the
	 * worker responds, the asker is never told.
	 *
	 * Routed here rather than from the worker because the plane already receives every agent's
	 * events and already knows how to reach the others; the worker would need a second RPC channel
	 * to do the same job.
	 *
	 * A reply is delivered as an ordinary message, so it appears in the asker's thread and wakes it
	 * exactly like any other — carrying the hop count that eventually ends the exchange.
	 */
	private async deliverAgentReply(fromId: string, data: unknown): Promise<void> {
		const d = data as { replyTo?: string; result?: string; taskId?: string }
		const to = agentFromSession(d?.replyTo)
		if (!to || !d.result?.trim()) return
		try {
			const reg = await this.manager.readRegistry()
			const sender = reg.agents.find((a) => a.id === fromId)
			const target = reg.agents.find((a) => a.id === to || a.name === to)
			if (!target || target.id === fromId) return
			// The answer travels back with the same provenance it arrived with, so the run that
			// receives it knows who has been waiting all along.
			const finished = await this.taskFacts(fromId, d.taskId)
			const fromName = sender?.name ?? fromId

			// One question, one answer. The person asked for a thing; this reply is that thing.
			//
			// The relay address is spent here, and does not travel on with the message. It used
			// to, and because it did, every later turn still counted as "on behalf of" the
			// person: the colleagues would wind the conversation down between themselves —
			// "sounds good", "happy to help" — and each of those was relayed too. Provenance
			// says who was waiting, not that they are owed a copy of everything said afterwards.
			//
			// The agents may keep talking. That conversation is theirs, it stays in their thread,
			// and the hop budget ends it.
			const answering = Boolean(finished.relayTo) && !agentFromSession(finished.relayTo)
			await this.callAgent(target.id, 'agent_message', {
				from: fromName,
				text: d.result,
				hops: finished.hops,
				relayTo: answering ? undefined : finished.relayTo,
			})

			// Put the answer in front of the person who asked for it.
			//
			// Telling the agent to relay did not work: asked to find something out from a
			// colleague, it would ask, get the answer, reply to the colleague, and leave the
			// person waiting — through two rounds of increasingly direct instruction. The reply
			// arrives in a run that is *addressed to the colleague*, so relaying is a thing the
			// model has to remember to do, and it does not.
			//
			// So delivery no longer depends on remembering. It only fires when a person's session
			// started the chain, which is precisely the case where somebody is waiting; agent
			// conversations nobody asked for stay out of the chat.
			if (answering) {
				await this.callAgent(target.id, 'thread_append', {
					sessionId: finished.relayTo,
					role: 'brain',
					content: `**${fromName}** replied:\n\n${d.result}`,
					notify: true,
				}).catch(() => undefined)
			}
		} catch {
			// A reply that cannot be delivered must not take the sending agent down with it. The
			// answer is still in the worker's own transcript.
		}
	}

	/** What a finished task's reply needs to carry onward: how deep it is, and who is waiting. */
	private async taskFacts(
		agentId: string,
		taskId?: string,
	): Promise<{ hops: number; relayTo?: string }> {
		if (!taskId) return { hops: 0 }
		try {
			const t = (await this.callAgent(agentId, 'task_status', { taskId })) as {
				hops?: number
				relayTo?: string
			}
			return { hops: Number(t?.hops) || 0, relayTo: t?.relayTo || undefined }
		} catch {
			return { hops: 0 }
		}
	}

	async handleOrchestrateRequest(
		senderId: string,
		req: { id: number; method: string; params?: Record<string, unknown> },
		reply?: (msg: { cres: { id: number; result?: unknown; error?: string } }) => void,
	): Promise<void> {
		const send =
			reply ??
			((msg: { cres: { id: number; result?: unknown; error?: string } }): void => {
				const child = this.children.get(senderId)
				if (!child?.proc.connected) return // sender exited mid-request — nothing to reply to
				try {
					child.proc.send?.(msg)
				} catch {
					/* channel closed between the check and the send */
				}
			})
		try {
			const reg = await this.manager.readRegistry()
			const sender = reg.agents.find((s) => s.id === senderId)
			// Messaging is not orchestration. Talking to a colleague is open to every agent;
			// assigning them work, rewriting their identity or restarting them is not, and that
			// whole `agent_*` family stays behind the flag. The split is the caller's intent, not
			// the transport — both arrive on the same channel, so the exemption is named here.
			if (req.method !== 'message' && sender?.orchestrator !== true) {
				throw new Error(`Agent "${senderId}" is not an orchestrator`)
			}
			const result = await this.dispatchOrchestrate(senderId, req.method, req.params ?? {})
			send({ cres: { id: req.id, result } })
		} catch (err) {
			send({ cres: { id: req.id, error: err instanceof Error ? err.message : String(err) } })
		}
	}

	/** Execute one orchestrator method. Targets resolve by id OR name; calls use the id. */
	private async dispatchOrchestrate(
		senderId: string,
		method: string,
		params: Record<string, unknown>,
	): Promise<unknown> {
		if (method === 'list') return this.listAgents()
		if (method === 'create') return this.createAgent(params.name as string)

		const t = params.target as string
		if (!t) {
			throw new Error(
				'Missing "target" — pass the agent id or name from agent_list, e.g. {"target":"video-editor"}',
			)
		}
		const reg = await this.manager.readRegistry()
		const entry = reg.agents.find((s) => s.id === t || s.name === t)
		if (!entry) throw new Error(`Agent not found: ${t}`)
		// Who is asking, by the name a sibling would recognise — the id is an opaque uuid.
		const senderName = reg.agents.find((a) => a.id === senderId)?.name ?? senderId
		if (['start', 'stop', 'restart'].includes(method) && entry.id === senderId) {
			throw new Error(`Refusing to ${method} the orchestrator itself`)
		}
		switch (method) {
			case 'state':
				return this.summarizeState(await this.callAgent(entry.id, 'state'))
			case 'task_status':
				return this.callAgent(entry.id, 'task_status', { taskId: params.taskId })
			case 'submit': {
				const input = params.input
				if (typeof input !== 'string' || input.trim() === '') {
					throw new Error(
						'Missing "input" — the task brief to send to the agent, e.g. {"target":"video-editor","input":"Summarize your last project"}',
					)
				}
				// `source: 'agent'` — an orchestrator brief is machine-to-machine work, not a person
				// typing in the chat. Without it these arrived as `user` tasks, indistinguishable
				// from your own messages: they raised unread chat badges you could never clear
				// (the session is one you never open), and they bypassed any `toolProfiles.agent`
				// restrictions meant for exactly this traffic.
				// Record who asked, so the answer can find its way home.
				//
				// Delegation is the case this matters most for and the one it was missing from: a
				// coordinator sent a brief, the worker's reply landed in the worker's own session,
				// and the coordinator learned nothing unless it remembered to poll. Naming the
				// sender gives the run a reply address on the far side, and the finished answer is
				// delivered back as a message — read, not polled for.
				return this.callAgent(entry.id, 'submit', {
					input,
					sessionId: params.sessionId,
					source: 'agent',
					fromAgent: senderName,
				})
			}
			case 'read_config':
				return this.callAgent(entry.id, 'read_config')
			case 'write_config':
				// The target's adapter guards apply (demo mode, sandbox-weakening refusal).
				return this.callAgent(entry.id, 'write_config', { config: params.config })
			case 'read_identity':
				return this.callAgent(entry.id, 'read_identity')
			case 'write_identity':
				return this.callAgent(entry.id, 'write_identity', {
					filename: params.filename,
					content: params.content,
				})
			case 'restart':
				return this.callAgent(entry.id, 'restart')
			case 'start':
				return this.startAgent(entry.id)
			case 'stop':
				return this.stopAgent(entry.id)
			case 'message': {
				// Deliver into the recipient's conversation with the sender, and wake it to read —
				// the same treatment a person's chat message already gets. An agent's word should
				// not be second-class to a human's just because it came over a different channel.
				const hops = Number(params.hops) || 0
				const body = String(params.text ?? '')
				const delivered = await this.callAgent(entry.id, 'agent_message', {
					from: senderName,
					text: body,
					hops,
					relayTo: typeof params.relayTo === 'string' ? params.relayTo : undefined,
				})
				// Keep the sender's own copy of the thread. The recipient's side is written by the
				// run this wakes, but the sender is calling from some other conversation, so without
				// this its thread opens on the reply and the question it asked is nowhere.
				await this.callAgent(senderId, 'thread_append', {
					sessionId: agentSessionId(entry.name),
					role: 'brain',
					content: body,
				}).catch(() => undefined)
				return delivered
			}
			default:
				// Deliberately no 'remove' — destroying an agent stays a human decision.
				throw new Error(`Unknown orchestrate method: ${method}`)
		}
	}

	/** Trim an agent's full dashboard state down to an LLM-friendly summary. */
	private summarizeState(raw: unknown): Record<string, unknown> {
		const s = (raw ?? {}) as Record<string, unknown>
		const paws = Array.isArray(s.paws) ? (s.paws as Array<Record<string, unknown>>) : []
		const skills = Array.isArray(s.skills) ? (s.skills as Array<Record<string, unknown>>) : []
		const tasks = Array.isArray(s.tasks) ? (s.tasks as Array<Record<string, unknown>>) : []
		const schedules = Array.isArray(s.schedules)
			? (s.schedules as Array<Record<string, unknown>>)
			: []
		const recent = [...tasks]
			.sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')))
			.slice(0, ORCH_STATE_TASKS)
			.map((t) => ({
				id: t.id,
				source: t.source,
				status: t.status,
				input:
					typeof t.input === 'string' && t.input.length > ORCH_INPUT_CLIP
						? `${t.input.slice(0, ORCH_INPUT_CLIP)}…`
						: t.input,
				createdAt: t.createdAt,
				completedAt: t.completedAt ?? null,
			}))
		return {
			toolCount: Array.isArray(s.tools) ? s.tools.length : 0,
			paws: paws.map((p) => ({ name: p.name, healthy: p.healthy })),
			skills: {
				active: skills.filter((k) => k.active).map((k) => k.name),
				inactive: skills
					.filter((k) => !k.active)
					.map((k) => ({ name: k.name, missingTools: k.missingTools })),
			},
			tasks: recent,
			queuedCount: tasks.filter((t) => t.status === 'queued').length,
			runningCount: tasks.filter((t) => t.status === 'running').length,
			schedules: schedules.map((c) => ({ id: c.id, cron: c.cron, nextRun: c.nextRun })),
		}
	}

	private onChildMessage(agentId: string, msg: unknown): void {
		const m = msg as {
			id?: number
			result?: unknown
			error?: string
			event?: string
			data?: unknown
		}
		if (m == null) return
		if ((m as { ready?: boolean }).ready) {
			this.children.get(agentId)?.markReady()
			return
		}
		// Reverse-RPC: an agent asking the control plane to act on its siblings.
		const creq = (m as { creq?: { id: number; method: string; params?: Record<string, unknown> } })
			.creq
		if (creq && typeof creq.id === 'number' && typeof creq.method === 'string') {
			void this.handleOrchestrateRequest(agentId, creq)
			return
		}
		if (m.id !== undefined && (m.result !== undefined || m.error !== undefined)) {
			const child = this.children.get(agentId)
			const pending = child?.pending.get(m.id)
			if (pending && child) {
				child.pending.delete(m.id)
				clearTimeout(pending.timeout)
				if (m.error) pending.reject(new Error(m.error))
				else pending.resolve(m.result)
			}
			return
		}
		if (m.event) {
			this.eventLog.append(m.event, m.data, agentId)
			this.server?.broadcast('event', m.data, m.event, agentId)
			if (m.event === 'task:completed') void this.deliverAgentReply(agentId, m.data)
			this.scheduleStateRefresh(agentId)
		}
	}

	private onChildExit(agentId: string): void {
		const child = this.children.get(agentId)
		if (child) {
			for (const p of child.pending.values()) {
				clearTimeout(p.timeout)
				p.reject(new Error('Agent engine exited'))
			}
			this.children.delete(agentId)
		}
		logger.info(`Agent "${agentId}" engine exited`)
		this.broadcastAgents()
	}

	/** Coalesced per-agent state refresh after a burst of bus events. */
	private scheduleStateRefresh(agentId: string): void {
		if (this.refreshTimers.has(agentId)) return
		const timer = setTimeout(() => {
			this.refreshTimers.delete(agentId)
			this.callAgent(agentId, 'state')
				.then((state) => this.server?.broadcast('state', state, undefined, agentId))
				.catch(() => {})
		}, STATE_DEBOUNCE_MS)
		this.refreshTimers.set(agentId, timer)
	}

	private broadcastAgents(): void {
		this.listAgents()
			.then((agents) => this.server?.broadcast('agents', agents))
			.catch(() => {})
	}

	async shutdown(): Promise<void> {
		for (const child of this.children.values()) {
			child.proc.kill('SIGTERM')
		}
		this.children.clear()
		await this.eventLog.close()
		await this.server?.close()
	}
}
